import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, filter, tap, take } from 'rxjs/operators';
import * as moment from 'moment';
import { ProductsOpenPlatformService, ProductService, ProductResp, ProductsResponseOP } from '@cemex-core/cmx-api';
import { ProductReq } from '@cemex-core/cmx-api/models/product/product-req';
import {
  OrderBuilderState,
  getOrderBuilderState,
  getJobsiteSelected,
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  getLocationPage,
  getActiveExperienceCode,
} from '@ordersnproducts/modules/order-builder/reducers/index';
import { getOpenPlatformProducts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import {
  ProductsActionTypes,
  ProductsLoadAction,
  ProductsLoadCompleteAction,
  ProductsResetAction,
  PCMLoadProductFromAPIAction,
  PCMLoadProductSuccessAction,
  PCMLoadProductFailureAction,
  PCMSelectProductAction,
  LoadUnitsByProductCompleteAction,
  LoadUnitsByProductFailureAction,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/products.actions';
import { RmsSpecificationActionTypes } from './../../actions/rms-specification.actions';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { ProductOPReq } from '@cemex-core/cmx-api/models/products-op/product-op-req';
import { SALES_DOCUMENT_TYPE_ID } from '@ordersnproducts/modules/shared/constants/sales-document-type-id';
import { Router } from '@angular/router';

@Injectable()
export class ProductsOpenPlatformEffects {
  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private productsOpenPlatformApi: ProductsOpenPlatformService,
    private productsApi: ProductService,
    private router: Router,
  ) {
    //
  }

  PCMLoadProduct: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActionTypes.PCM_LOAD_PRODUCT),
      switchMap((action: any) => {
        return this.store.select(getOpenPlatformProducts).pipe(
          filter((products) => products !== undefined),
          map((products) => {
            const product = products.find((item) => item.productCode === action.payload.productCode);
            if (product) {
              return new PCMLoadProductSuccessAction(product);
            } else {
              return new PCMLoadProductFromAPIAction({ productCode: action.payload.productCode });
            }
          }),
        );
      }),
    ),
  );

  PCMLoadProductFromAPI: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActionTypes.PCM_LOAD_PRODUCT_FROM_API),
      switchMap((action: any) => {
        return this.store.select(getOrderBuilderState).pipe(
          take(1),
          map((state) => {
            return {
              shipmentLocationId: state.locationPage.jobsite.selected.shipmentLocationId,
              shippingConditionId: state.businessLinePage.shippingCondition.selected.shippingConditionId,
              productLineId: state.businessLinePage.businessLine.selected.productTypeId,
              salesDocumentTypeId: SALES_DOCUMENT_TYPE_ID.COUNTRY_PRODUCTS,
              productCode: action.payload.productCode,
            } as ProductReq;
          }),
          switchMap((request: ProductReq) => {
            return this.productsApi.getProduct(request).pipe(
              map((response: ProductResp) => {
                if (!response || !response.products || !(response.products.length > 0)) {
                  return new PCMLoadProductFailureAction();
                }
                return new PCMSelectProductAction({
                  productCode: response.products[0].commercialCode,
                  productDesc: response.products[0].commercialDesc,
                });
              }),
              catchError(() => of(new PCMLoadProductFailureAction())),
            );
          }),
        );
      }),
    ),
  );

  loadContractProduct$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RmsSpecificationActionTypes.INIT),
        tap(() => {
          this.store
            .select(getActiveExperienceCode)
            .pipe(
              take(1),
              filter(
                (experienceCode) =>
                  experienceCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK ||
                  experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
                  experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK ||
                  experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK,
              ),
            )
            .subscribe(() => this.store.dispatch(new ProductsLoadAction()));
        }),
      ),
    { dispatch: false },
  );

  getContractProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActionTypes.LOAD),
      withLatestFrom(
        this.store.pipe(select(getJobsiteSelected)),
        this.store.pipe(select(getSelectedBusinessLine)),
        this.store.pipe(select(getSelectedShippingConditions)),
        this.store.pipe(select(getLocationPage)),
        this.store.pipe(select(getActiveExperienceCode)),
      ),
      filter(
        ([action, jobsite, businessLine, shippingCondition, locationPageState, experienceCode]) =>
          !!shippingCondition && !!shippingCondition.shippingConditionId,
      ), // TODO: CHECK EMPTY SOMETIMES
      switchMap(([action, jobsite, businessLine, shippingCondition, locationPageState, experienceCode]) => {
        const productTypeId = Number(businessLine.productTypeId);
        const shippingConditionId = shippingCondition.shippingConditionId;
        const jobsiteCode = jobsite.shipmentLocationCode;
        const jobsiteId = jobsite.shipmentLocationId;
        const deliveryDate = moment().format('YYYY-MM-DD');
        const calendar = locationPageState.calendars.calendars[0];
        let subGroup = '';
        if (calendar) {
          subGroup = calendar.subGroup.subGroupCode;
        }
        return this.productsOpenPlatformApi
          .getProducts(
            this.makePayload(
              experienceCode,
              productTypeId,
              shippingConditionId,
              jobsiteId,
              jobsiteCode,
              deliveryDate,
              subGroup,
            ),
          )
          .pipe(
            map((response: ProductsResponseOP) => {
              if (this.router.url.includes('calendar-view-2')) {
                return new ProductsResetAction();
              }
              return new ProductsLoadCompleteAction(response);
            }),
            catchError((error) => {
              return of(new ProductsResetAction());
            }),
          );
      }),
    ),
  );

  getUnitsByProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductsActionTypes.LOAD_UNITS_BY_PRODUCT),
      switchMap((action: any) => {
        const params = {
          productCode: action.payload.productCode,
        };
        return this.productsApi.getUnitsV4(params).pipe(
          map((response) => {
            return new LoadUnitsByProductCompleteAction({
              productCode: action.payload.productCode,
              units: response.units,
            });
          }),
          catchError((error) => {
            return of(
              new LoadUnitsByProductFailureAction({
                productCode: action.payload.productCode,
                error: error,
              }),
            );
          }),
        );
      }),
    ),
  );

  private makePayload(
    experienceCode: string,
    productTypeId: number,
    shippingConditionId: number,
    jobsiteId: number,
    jobsiteCode: string,
    deliveryDate: string,
    subGroup: string,
  ): ProductOPReq {
    if (
      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
      experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
    ) {
      return {
        productTypeId: productTypeId,
        shippingConditionId: shippingConditionId,
        jobsiteCode,
        deliveryDate: deliveryDate,
        subGroup: subGroup,
      };
    } else if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
      return {
        productTypeId: productTypeId,
        jobsiteId: jobsiteId,
        fetch: 10000,
        subGroup: '0',
      };
    } else {
      return {
        productTypeId: productTypeId,
        jobsiteId: jobsiteId,
        fetch: 10000,
      };
    }
  }
}
